import React, { useState } from "react";
import PropTypes from "prop-types";
import PairingInterface from "./PairingInterface";
import PairingExplorer from "./PairingExplorer";
import PairingAnalytics from "./PairingAnalytics";
import PairingFeedback from "./PairingFeedback";
import "./Phase2PairingDemo.css";

/**
 * Phase2PairingDemo Component - Phase 2.2 Implementation
 *
 * Comprehensive demo showcasing all Phase 2.2 pairing components
 * working together in an integrated system.
 */
const Phase2PairingDemo = ({
  restaurantId = "demo-restaurant",
  isAdmin = false,
}) => {
  const [activeTab, setActiveTab] = useState("interface");
  const [selectedPairing, setSelectedPairing] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  // Mock data for demonstration
  const mockBeverage = {
    id: "wine-cab-sauv-reserve",
    name: "Cabernet Sauvignon Reserve 2018",
    type: "Red Wine",
    category: "Wine",
    producer: "Napa Valley Estates",
    region: "Napa Valley, California",
    vintage: 2018,
    abv: 14.5,
    description:
      "Full-bodied red with rich dark fruit flavors and oak complexity",
    price: 85,
    rating: 4.6,
  };

  const mockBeverages = [
    mockBeverage,
    {
      id: "wine-pinot-grigio",
      name: "Pinot Grigio 2022",
      type: "White Wine",
      category: "Wine",
      producer: "Villa Italiana",
      region: "Veneto, Italy",
      vintage: 2022,
      abv: 12.5,
      description: "Crisp and refreshing with citrus and mineral notes",
      price: 42,
      rating: 4.3,
    },
    {
      id: "cocktail-old-fashioned",
      name: "Old Fashioned",
      type: "Whiskey Cocktail",
      category: "Cocktail",
      description: "Classic bourbon cocktail with bitters and orange",
      price: 16,
      rating: 4.7,
    },
  ];

  const mockMenuItems = [
    {
      id: "steak-ribeye",
      name: "Grilled Ribeye Steak",
      category: "Main Course",
      description: "12oz prime ribeye with herb butter",
      price: 48,
    },
    {
      id: "salmon-pan-seared",
      name: "Pan-Seared Atlantic Salmon",
      category: "Main Course",
      description: "Fresh salmon with lemon herb crust",
      price: 34,
    },
    {
      id: "pasta-carbonara",
      name: "Pasta Carbonara",
      category: "Main Course",
      description: "Traditional Italian carbonara with pancetta",
      price: 28,
    },
  ];

  const tabs = [
    {
      id: "interface",
      label: "🍽️ Pairing Interface",
      description: "AI-powered pairing suggestions",
    },
    {
      id: "explorer",
      label: "🔍 Pairing Explorer",
      description: "Discovery and search features",
    },
    {
      id: "feedback",
      label: "💬 Guest Feedback",
      description: "Rating and feedback system",
    },
    ...(isAdmin
      ? [
          {
            id: "analytics",
            label: "📊 Analytics Dashboard",
            description: "Performance insights",
          },
        ]
      : []),
  ];

  const handlePairingSelect = (pairing) => {
    setSelectedPairing(pairing);
    setShowFeedback(true);
  };

  const handlePairingFound = (pairing) => {
    setSelectedPairing(pairing);
    console.log("Pairing found:", pairing);
  };

  const handleFeedbackSubmit = (feedbackData) => {
    console.log("Feedback submitted:", feedbackData);
    setShowFeedback(false);
    // In a real app, this would update analytics
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "interface":
        return (
          <div className="tab-content">
            <div className="demo-section">
              <h3>Interactive Pairing Suggestions</h3>
              <p className="section-description">
                AI-curated food pairings for each beverage with confidence
                scores and detailed explanations.
              </p>

              <div className="beverage-showcase">
                <h4>Sample Beverage:</h4>
                <div className="beverage-card">
                  <h5>{mockBeverage.name}</h5>
                  <p>{mockBeverage.description}</p>
                  <div className="beverage-details">
                    <span>{mockBeverage.type}</span>
                    <span>{mockBeverage.region}</span>
                    <span>{mockBeverage.abv}% ABV</span>
                  </div>
                </div>
              </div>

              <PairingInterface
                beverage={mockBeverage}
                restaurantId={restaurantId}
                menuItems={mockMenuItems}
                onPairingSelect={handlePairingSelect}
              />
            </div>
          </div>
        );

      case "explorer":
        return (
          <div className="tab-content">
            <div className="demo-section">
              <h3>Pairing Discovery System</h3>
              <p className="section-description">
                Find perfect beverage pairings by food items, occasions, or
                dietary preferences.
              </p>

              <PairingExplorer
                restaurantId={restaurantId}
                beverages={mockBeverages}
                menuItems={mockMenuItems}
                onPairingFound={handlePairingFound}
              />
            </div>
          </div>
        );

      case "feedback":
        return (
          <div className="tab-content">
            <div className="demo-section">
              <h3>Guest Feedback System</h3>
              <p className="section-description">
                Collect guest ratings and feedback to improve pairing
                recommendations.
              </p>

              <div className="feedback-demo">
                <h4>Standard Feedback Form:</h4>
                <PairingFeedback
                  restaurantId={restaurantId}
                  pairingId="demo-pairing-1"
                  beverageName={mockBeverage.name}
                  foodName="Grilled Ribeye Steak"
                  onFeedbackSubmit={handleFeedbackSubmit}
                />

                <h4>Inline Quick Feedback:</h4>
                <PairingFeedback
                  restaurantId={restaurantId}
                  pairingId="demo-pairing-2"
                  beverageName="Pinot Grigio"
                  foodName="Pan-Seared Salmon"
                  showInline={true}
                  onFeedbackSubmit={handleFeedbackSubmit}
                />
              </div>
            </div>
          </div>
        );

      case "analytics":
        return (
          <div className="tab-content">
            <div className="demo-section">
              <h3>Analytics Dashboard</h3>
              <p className="section-description">
                Comprehensive insights into pairing performance and guest
                satisfaction.
              </p>

              <PairingAnalytics restaurantId={restaurantId} timeRange="30d" />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="phase2-pairing-demo">
      <div className="demo-header">
        <h1>🍾 Phase 2.2: Intelligent Pairing System</h1>
        <p className="demo-subtitle">
          Complete AI-powered beverage pairing system with interactive UI
          components
        </p>
        {isAdmin && <div className="admin-badge">🔧 Admin View</div>}
      </div>

      <div className="demo-navigation">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`nav-tab ${activeTab === tab.id ? "active" : ""}`}
          >
            <span className="tab-label">{tab.label}</span>
            <span className="tab-description">{tab.description}</span>
          </button>
        ))}
      </div>

      <div className="demo-body">{renderTabContent()}</div>

      {/* Feedback Modal */}
      {showFeedback && selectedPairing && (
        <div
          className="feedback-modal-overlay"
          onClick={() => setShowFeedback(false)}
        >
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Rate This Pairing</h3>
              <button
                onClick={() => setShowFeedback(false)}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <PairingFeedback
                restaurantId={restaurantId}
                pairingId={selectedPairing.id || "demo-pairing"}
                beverageName={mockBeverage.name}
                foodName={selectedPairing.foodName || selectedPairing.name}
                onFeedbackSubmit={handleFeedbackSubmit}
              />
            </div>
          </div>
        </div>
      )}

      <div className="demo-footer">
        <div className="implementation-status">
          <h4>✅ Phase 2.2 Implementation Complete</h4>
          <div className="completed-features">
            <span className="feature">🍽️ Interactive Pairing Interface</span>
            <span className="feature">🔍 Smart Pairing Discovery</span>
            <span className="feature">📊 Analytics Dashboard</span>
            <span className="feature">💬 Guest Feedback System</span>
          </div>
        </div>
      </div>
    </div>
  );
};

Phase2PairingDemo.propTypes = {
  restaurantId: PropTypes.string,
  isAdmin: PropTypes.bool,
};

Phase2PairingDemo.defaultProps = {
  restaurantId: "demo-restaurant",
  isAdmin: false,
};

export default Phase2PairingDemo;
